import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Dialog, Portal } from '@chakra-ui/react';
import { subscribeUpgradeRequired, type UpgradeRequiredDetail } from '@/lib/billing-events';
import { ExploreUpgradeDialogContent } from './components/ExploreUpgradeDialogContent';
import { ManageUpgradeDialogContent } from './components/ManageUpgradeDialogContent';

interface UpgradeDialogContentProps {
    detail: UpgradeRequiredDetail;
    onClose: () => void;
    onViewPlans: () => void;
}

const billingPathByOwnerType: Record<string, (ownerId: string) => string> = {
    group: (ownerId) => `/groups/${ownerId}/preferences/billing`,
    home: (ownerId) => `/home/${ownerId}/preferences/billing`,
};

/** The plan picker this 402 should send the user to, or undefined if unknown. */
function billingPathFor(detail: UpgradeRequiredDetail): string | undefined {
    return detail.ownerId ? billingPathByOwnerType[detail.ownerType]?.(detail.ownerId) : undefined;
}

/**
 * Selects the plan-specific content while leaving the global dialog lifecycle
 * and route handling in one place.
 */
function UpgradeDialogContent({ detail, onClose, onViewPlans }: UpgradeDialogContentProps) {
    const canViewPlans = Boolean(billingPathFor(detail));

    if (detail.ownerType === 'home') {
        return (
            <ManageUpgradeDialogContent
                detail={detail}
                onClose={onClose}
                onViewPlans={onViewPlans}
                canViewPlans={canViewPlans}
            />
        );
    }

    return (
        <ExploreUpgradeDialogContent
            detail={detail}
            onClose={onClose}
            onViewPlans={onViewPlans}
            canViewPlans={canViewPlans}
        />
    );
}

/**
 * Global upsell modal. Mounted once near the app root; opens whenever any API
 * call returns a 402 UPGRADE_REQUIRED, letting the user jump to the plan picker
 * for the relevant owner scope.
 */
export function UpgradeDialog() {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const [detail, setDetail] = useState<UpgradeRequiredDetail | null>(null);

    // Resubscribed on navigation so the handler always sees the current path.
    useEffect(
        () =>
            subscribeUpgradeRequired((next) => {
                // Suppress when the user is already on the plan picker this modal
                // would send them to. Otherwise a 402 from a query still in flight
                // when they land there re-opens the upsell on top of the very page
                // they need to subscribe on — the modal loops and they stay stuck.
                if (billingPathFor(next) === pathname) {
                    return;
                }
                setDetail(next);
            }),
        [pathname],
    );

    const close = () => setDetail(null);

    const viewPlans = () => {
        const billingPath = detail ? billingPathFor(detail) : undefined;

        if (billingPath) {
            navigate(billingPath);
        }
        close();
    };

    if (detail === null) {
        return null;
    }

    return (
        <Dialog.Root
            open
            onOpenChange={(e) => (e.open ? undefined : close())}
            placement="center"
            role="alertdialog"
        >
            <Portal>
                <Dialog.Backdrop
                    bg="rgba(28,22,15,0.5)"
                    backdropFilter="blur(3px)"
                />
                <Dialog.Positioner>
                    <Dialog.Content
                        bg="transparent"
                        boxShadow="none"
                        padding={0}
                        maxW="none"
                        w="auto"
                    >
                        <UpgradeDialogContent
                            detail={detail}
                            onClose={close}
                            onViewPlans={viewPlans}
                        />
                    </Dialog.Content>
                </Dialog.Positioner>
            </Portal>
        </Dialog.Root>
    );
}
